// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from '@wandb/weave/components/Icon';
import React, {FC, useEffect, useState} from 'react';
import {useParams} from 'react-router';
// @todo: Migrate to 'Toast' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {toast} from 'react-toastify';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Modal, Table} from 'semantic-ui-react';

import {
  Secret,
  useDeleteSecretMutation,
  useInsertSecretMutation,
  useSecretsQuery,
} from '../../generated/graphql';
import {useViewer} from '../../state/viewer/hooks';
import {
  extractErrorMessageFromApolloError,
  propagateErrorsContext,
} from '../../util/errors';
import {maybePluralize} from '../../util/uihelpers';
import {SecretsDrawer} from './SecretsDrawer';
import * as TeamSettingsStyles from './TeamSettings.styles';
import * as S from './TeamSettingsSecrets.styles';
import {DrawerMode, PopupType} from './types';

const validateSecretName = (name: string) => {
  // Any alphanumeric+underscore string starting with an alphabet character or underscore
  const re = /^[a-zA-Z_][a-zA-Z0-9_]*$/;
  return re.test(String(name));
};

export const useSecrets = (entityName: string) => {
  const {data, loading, error, refetch} = useSecretsQuery({
    variables: {entityName},
  });
  const secrets = data?.entity?.secrets ?? [];

  if (loading || error || !data) {
    return {secrets: null, loading, error, refetch};
  }
  return {secrets, loading, error, refetch};
};

interface SecretsListProps {
  secrets: Secret[];
  onClickEdit: (s: Secret) => void;
  onClickDelete: (s: Secret) => void;
}

export const SecretsList: FC<SecretsListProps> = ({
  secrets,
  onClickEdit,
  onClickDelete,
}) => {
  return (
    <Table className="secrets--table" basic="very">
      <Table.Body>
        {secrets.map((secret: Secret, index: number) => {
          const date = new Date(secret.createdAt);
          const lastUpdatedDate = date.toDateString();
          return (
            <Table.Row key={secret.name}>
              <Table.Cell>
                <S.SecretNameCell>
                  <Icon name="key-admin-private" />
                  <S.SecretName title={secret.name}>{secret.name}</S.SecretName>
                </S.SecretNameCell>
              </Table.Cell>
              <Table.Cell>
                <S.SecretOptions>
                  <S.UpdatedDateText>
                    Last updated: {lastUpdatedDate}
                  </S.UpdatedDateText>
                  <Button
                    variant="ghost"
                    icon="pencil-edit"
                    onClick={() => onClickEdit(secret)}
                  />
                  <Button
                    variant="ghost"
                    icon="delete"
                    onClick={() => onClickDelete(secret)}
                  />
                </S.SecretOptions>
              </Table.Cell>
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table>
  );
};

interface DeleteSecretDialogProps {
  popupType: PopupType;
  secretToBeDeletedName: string;
  onClickDeleteSecret: () => void;
  onDeleteModalClose: () => void;
}

const DeleteSecretDialog: FC<DeleteSecretDialogProps> = ({
  popupType,
  secretToBeDeletedName,
  onClickDeleteSecret,
  onDeleteModalClose,
}) => {
  const isDeleteModalOpen = popupType === 'DeleteSecret';

  return (
    <Modal open={isDeleteModalOpen} onClose={onDeleteModalClose}>
      <Modal.Header style={{display: 'flex'}}>
        Delete secret "<S.SecretName>{secretToBeDeletedName}</S.SecretName>"?
      </Modal.Header>
      <Modal.Content>
        <p>
          This may affect webhooks and other integrations that rely on this
          secret. Failure to authenticate or authorize requests may result in
          errors or disruptions. Proceed with caution.
        </p>
      </Modal.Content>
      <Modal.Actions>
        <Button variant="ghost" onClick={onDeleteModalClose} className="mr-8">
          Cancel
        </Button>
        <Button variant="destructive" onClick={onClickDeleteSecret}>
          Delete
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export const TeamSettingsSecrets: FC = React.memo(() => {
  const viewer = useViewer();
  const {entityName: routeEntityName} = useParams<{entityName: string}>();
  const entityName = routeEntityName ?? viewer?.username ?? '';

  const [popupType, setPopupType] = useState<PopupType>(null);
  const [secretToBeDeletedName, setSecretToBeDeletedName] = useState('');
  const [inputError, setInputError] = useState(false);
  const [secretName, setSecretName] = useState('');
  const [secretValue, setSecretValue] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [buttonClickable, setButtonClickable] = useState(false);
  const [drawerMode, setDrawerMode] = useState<DrawerMode>('Creating');
  const {secrets, refetch} = useSecrets(entityName);
  const [insertSecret] = useInsertSecretMutation({
    context: propagateErrorsContext(),
  });
  const [deleteSecret] = useDeleteSecretMutation({
    context: propagateErrorsContext(),
  });

  useEffect(() => {
    setButtonClickable(
      secretName !== '' && secretValue !== '' && validateSecretName(secretName)
    );
  }, [secretName, secretValue]);

  const onClickNewSecret = () => {
    setDrawerMode('Creating');
    setPopupType('AddSecret');
  };

  const onClickEdit = (secret: Secret) => {
    setSecretName(secret.name);
    setDrawerMode('Editing');
    setPopupType('AddSecret');
  };

  const onClickDelete = (secret: Secret) => {
    setSecretToBeDeletedName(secret.name);
    setPopupType('DeleteSecret');
  };

  const onClickAddSecret = async () => {
    try {
      await insertSecret({
        variables: {entityName, secretName, secretValue},
      });
      toast.success(<S.SecretName>Added secret {secretName}</S.SecretName>);
    } catch (err) {
      const errMsg = extractErrorMessageFromApolloError(err);
      console.error(errMsg);
      toast.error(`Problem adding secret ${secretName}: ${errMsg}`);
    }
    onDrawerClose();
    refetch();
  };

  const onClickDeleteSecret = async () => {
    try {
      await deleteSecret({
        variables: {entityName, secretName: secretToBeDeletedName},
      });
      toast.success(`Deleted secret ${secretToBeDeletedName}`);
    } catch (err) {
      const errMsg = extractErrorMessageFromApolloError(err);
      console.error(errMsg);
      toast.error(
        `Problem deleting secret ${secretToBeDeletedName}: ${errMsg}`
      );
    }
    setPopupType(null);
    refetch();
  };

  const onDrawerClose = () => {
    setSecretName('');
    setSecretValue('');
    setShowSecret(false);
    setPopupType(null);
  };

  const onDeleteModalClose = () => {
    setPopupType(null);
  };

  const handleSecretNameChange = (name: string) => {
    setSecretName(name);
    setInputError(!validateSecretName(name));
  };

  const handleSecretValueChange = (value: string) => setSecretValue(value);

  return (
    <>
      <TeamSettingsStyles.SectionHeader smallMargin>
        Team secrets
      </TeamSettingsStyles.SectionHeader>
      <TeamSettingsStyles.SectionSubheader smallMargin>
        Secrets are team-level variables that let you obfuscate private strings
        such as credentials, API keys, passwords, tokens, etc.
      </TeamSettingsStyles.SectionSubheader>
      <S.SecretsSummary>
        {secrets &&
          secrets?.length > 0 &&
          `${maybePluralize(secrets?.length, 'secret')}`}
        <Button
          variant="primary"
          size="small"
          className="p-8"
          onClick={onClickNewSecret}>
          New secret
        </Button>
      </S.SecretsSummary>
      <SecretsList
        secrets={secrets ?? []}
        onClickEdit={onClickEdit}
        onClickDelete={onClickDelete}
      />
      <DeleteSecretDialog
        popupType={popupType}
        secretToBeDeletedName={secretToBeDeletedName}
        onClickDeleteSecret={onClickDeleteSecret}
        onDeleteModalClose={onDeleteModalClose}
      />
      <SecretsDrawer
        popupType={popupType}
        drawerMode={drawerMode}
        secretName={secretName}
        secretValue={secretValue}
        showSecret={showSecret}
        handleSecretValueChange={handleSecretValueChange}
        handleSecretNameChange={handleSecretNameChange}
        setShowSecret={setShowSecret}
        inputError={inputError}
        buttonClickable={buttonClickable}
        onClickAddSecret={onClickAddSecret}
        onDrawerClose={onDrawerClose}
      />
    </>
  );
});
